import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send,
  Eye,
  MousePointer,
  AlertCircle,
  MessageSquare,
  Users,
  Loader2,
} from "lucide-react";
import { colors, fonts, CONTACT_TYPES } from "../styles/theme";
import { StatCard, Badge, Table } from "../components/ui";
import { ContactDrawer } from "../components/ContactDrawer";
import {
  fetchEngagementStats,
  fetchNewsletters,
  deriveContactType,
  displayName,
  initials,
  fmtDate,
} from "../services/api";

const ROW_OPTIONS = [10, 20, 50, 100];

function RowSelector({ value, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 12, color: colors.textSecondary }}>Show</span>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 4,
          padding: "3px 6px",
          fontSize: 12,
          color: colors.text,
          fontFamily: fonts.body,
          cursor: "pointer",
        }}
      >
        {ROW_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      <span style={{ fontSize: 12, color: colors.textSecondary }}>rows</span>
    </div>
  );
}

export default function Engagement() {
  const navigate = useNavigate();
  const [eStats, setEStats] = useState(null);
  const [topContacts, setTopContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [sortBy, setSortBy] = useState("total");
  const [contactRows, setContactRows] = useState(20);
  const [campaignRows, setCampaignRows] = useState(20);
  const contactsRef = useRef(null);

  const handleCardClick = (metric) => {
    setSortBy(metric);
    contactsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    Promise.all([
      fetchEngagementStats(),
      fetchNewsletters({ status: "completed" }),
    ])
      .then(([engagement, campaignData]) => {
        setEStats(engagement);
        setTopContacts(engagement.topContacts || []);
        setCampaigns(
          (campaignData.campaigns || [])
            .sort((a, b) => (b.recipients || 0) - (a.recipients || 0)),
        );
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // useMemo MUST be called before any conditional returns (Rules of Hooks)
  const sortedContacts = useMemo(() => {
    const list = [...topContacts];
    switch (sortBy) {
      case "opens":
        list.sort((a, b) => (b.engagement?.opens || 0) - (a.engagement?.opens || 0));
        break;
      case "clicks":
        list.sort((a, b) => (b.engagement?.clicks || 0) - (a.engagement?.clicks || 0));
        break;
      case "delivered":
        list.sort((a, b) => (b.engagement?.delivered || 0) - (a.engagement?.delivered || 0));
        break;
      case "openRate":
        list.sort((a, b) => (b.engagement?.openRate || 0) - (a.engagement?.openRate || 0));
        break;
      default: // "total" — opens + clicks
        list.sort(
          (a, b) =>
            (b.engagement?.opens || 0) + (b.engagement?.clicks || 0) -
            ((a.engagement?.opens || 0) + (a.engagement?.clicks || 0)),
        );
    }
    return list;
  }, [topContacts, sortBy]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <Loader2
          size={24}
          color={colors.accent}
          className="spin"
        />      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: colors.danger }}>
        <AlertCircle size={24} style={{ marginBottom: 8 }} />
        <div>{error}</div>
      </div>
    );
  }

  const stats = eStats || {};

  const sortLabel = {
    total: "Total Engagement",
    opens: "Opens",
    clicks: "Clicks",
    delivered: "Delivered",
    openRate: "Open Rate",
  };

  // ── Campaign table columns ──
  const campaignColumns = [
    {
      key: "name",
      label: "Campaign",
      sortValue: (row) => (row.name || "").toLowerCase(),
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          {row.subject && row.subject !== row.name && (
            <div
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                marginTop: 2,
              }}
            >
              {row.subject}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "recipients",
      label: "Delivered",
      sortValue: (row) => row.recipients || 0,
      render: (row) => (
        <span style={{ fontFamily: fonts.mono, fontSize: 12 }}>
          {row.recipients > 0 ? row.recipients.toLocaleString() : "\u2014"}
        </span>
      ),
    },
    {
      key: "totalCount",
      label: "Attempted",
      sortValue: (row) => row.totalCount || 0,
      render: (row) => (
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: 12,
            color: colors.textSecondary,
          }}
        >
          {row.totalCount > 0 ? row.totalCount.toLocaleString() : "\u2014"}
        </span>
      ),
    },
    {
      key: "failed",
      label: "Failed",
      sortValue: (row) => row.failed || 0,
      render: (row) => {
        if (!row.failed)
          return <span style={{ color: colors.textSecondary }}>{"\u2014"}</span>;
        return (
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: 12,
              color: colors.danger,
            }}
          >
            {row.failed.toLocaleString()}
          </span>
        );
      },
    },
    {
      key: "successRate",
      label: "Success %",
      sortValue: (row) => row.totalCount > 0 ? (row.recipients / row.totalCount) * 100 : 0,
      render: (row) => {
        const rate =
          row.totalCount > 0
            ? ((row.recipients / row.totalCount) * 100).toFixed(0)
            : 0;
        return (
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: 12,
              color: Number(rate) >= 90 ? colors.success : colors.textSecondary,
            }}
          >
            {rate}%
          </span>
        );
      },
    },
    {
      key: "date",
      label: "Date Sent",
      sortValue: (row) => { const d = row.sentAt || row.scheduledAt || row.createdAt; return d ? new Date(d).getTime() : 0; },
      render: (row) => {
        const d = row.sentAt || row.scheduledAt || row.createdAt;
        return (
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            {d ? fmtDate(d) : "\u2014"}
          </span>
        );
      },
    },
  ];

  // ── Top contacts table columns ──
  const contactColumns = [
    {
      key: "name",
      label: "Contact",
      sortValue: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim().toLowerCase(),
      render: (row) => {
        const type = deriveContactType(row.tags, row.ghlTags);
        const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: typeInfo.bg,
                color: typeInfo.color,
                fontFamily: fonts.heading,
                fontWeight: 700,
                fontSize: 11,
                flexShrink: 0,
              }}
            >
              {initials(row)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{displayName(row)}</div>
              {row.company && (
                <div
                  style={{
                    fontSize: 12,
                    color: colors.textSecondary,
                    marginTop: 1,
                  }}
                >
                  {row.company}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "opens",
      label: "Opens",
      sortValue: (row) => row.engagement?.opens || 0,
      render: (row) => (
        <span style={{ fontFamily: fonts.mono, fontSize: 12 }}>
          {(row.engagement?.opens || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: "clicks",
      label: "Clicks",
      sortValue: (row) => row.engagement?.clicks || 0,
      render: (row) => (
        <span style={{ fontFamily: fonts.mono, fontSize: 12 }}>
          {(row.engagement?.clicks || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: "delivered",
      label: "Delivered",
      sortValue: (row) => row.engagement?.delivered || 0,
      render: (row) => (
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: 12,
            color: colors.textSecondary,
          }}
        >
          {(row.engagement?.delivered || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: "openRate",
      label: "Open Rate",
      sortValue: (row) => row.engagement?.openRate || 0,
      render: (row) => (
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: 12,
            color:
              (row.engagement?.openRate || 0) >= 50
                ? colors.success
                : colors.textSecondary,
          }}
        >
          {row.engagement?.openRate || 0}%
        </span>
      ),
    },
    {
      key: "type",
      label: "Type",
      sortValue: (row) => (CONTACT_TYPES[deriveContactType(row.tags, row.ghlTags)]?.label || "subscriber").toLowerCase(),
      render: (row) => {
        const type = deriveContactType(row.tags, row.ghlTags);
        const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
        return (
          <Badge color={typeInfo.color} bg={typeInfo.bg}>
            {typeInfo.label}
          </Badge>
        );
      },
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1
          style={{
            fontFamily: fonts.heading,
            fontSize: 28,
            fontWeight: 700,
            color: colors.text,
            marginBottom: 4,
          }}
        >
          Engagement
        </h1>
        <p
          style={{
            fontFamily: fonts.body,
            fontSize: 14,
            color: colors.textSecondary,
          }}
        >
          Email performance across {(stats.contactsWithData || 0).toLocaleString()} contacts
        </p>
      </div>

      {/* Stat Cards */}
      <div
        style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}
      >
        <StatCard
          icon={Send}
          label="Total Delivered"
          value={(stats.totalDelivered || 0).toLocaleString()}
          change={`${(stats.contactsWithData || 0).toLocaleString()} contacts`}
          changeType="up"
          onClick={() => handleCardClick("delivered")}
          active={sortBy === "delivered"}
        />
        <StatCard
          icon={Eye}
          label="Opens"
          value={(stats.totalOpens || 0).toLocaleString()}
          change={`${stats.openRate || 0}% rate`}
          changeType="up"
          onClick={() => handleCardClick("opens")}
          active={sortBy === "opens"}
        />
        <StatCard
          icon={MousePointer}
          label="Clicks"
          value={(stats.totalClicks || 0).toLocaleString()}
          change={`${stats.clickRate || 0}% rate`}
          changeType="up"
          onClick={() => handleCardClick("clicks")}
          active={sortBy === "clicks"}
        />
        <StatCard
          icon={AlertCircle}
          label="Bounced"
          value={(stats.totalBounced || 0).toLocaleString()}
          change={`${stats.bounceRate || 0}% rate`}
          changeType="down"
          onClick={() => navigate("/email-health")}
        />
        <StatCard
          icon={MessageSquare}
          label="Replied"
          value={(stats.totalReplied || 0).toLocaleString()}
        />
        <StatCard
          icon={Users}
          label="Contacts Tracked"
          value={(stats.contactsWithData || 0).toLocaleString()}
        />
      </div>

      {/* Two-column layout: Contacts (left) | Campaigns (right) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          alignItems: "start",
        }}
      >
        {/* Top Engaged Contacts — LHS */}
        <div ref={contactsRef}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.text,
                  margin: 0,
                }}
              >
                Top Engaged Contacts
              </h2>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                sorted by {sortLabel[sortBy] || "Total Engagement"}
              </span>
            </div>
            <RowSelector value={contactRows} onChange={setContactRows} />
          </div>
          {sortedContacts.length > 0 ? (
            <Table
              columns={contactColumns}
              data={sortedContacts.slice(0, contactRows)}
              onRowClick={setSelectedContact}
            />
          ) : (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: colors.textSecondary,
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
              }}
            >
              No engagement data yet
            </div>
          )}
          {sortedContacts.length > contactRows && (
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8, textAlign: "right" }}>
              Showing {contactRows} of {sortedContacts.length}
            </div>
          )}
        </div>

        {/* Campaign Performance — RHS */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 18,
                fontWeight: 700,
                color: colors.text,
                margin: 0,
              }}
            >
              Campaign Performance
            </h2>
            <RowSelector value={campaignRows} onChange={setCampaignRows} />
          </div>
          {campaigns.length > 0 ? (
            <Table columns={campaignColumns} data={campaigns.slice(0, campaignRows)} />
          ) : (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: colors.textSecondary,
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
              }}
            >
              No completed campaigns yet
            </div>
          )}
          {campaigns.length > campaignRows && (
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8, textAlign: "right" }}>
              Showing {campaignRows} of {campaigns.length}
            </div>
          )}
        </div>
      </div>

      <ContactDrawer
        contact={selectedContact}
        onClose={() => setSelectedContact(null)}
      />
    </div>
  );
}
